import { useState } from 'react';

import { Modal } from '../ui';
import { api } from '../../lib/api';
import type { Funnel } from './types';
import { errorText } from './types';

const TURN_OFF_PHRASE = 'TURN OFF';

/**
 * The SSH funnel switch. Turning it on is guarded three ways before the server will
 * accept it: SUPERADMIN only, every readiness check passing, and the typed phrase.
 * Turning it off is one step removed (typed phrase, no readiness), so it is always
 * available to ADMIN and above.
 */
export function FunnelToggle({
  funnel,
  canTurnOn,
  canTurnOff,
  onChanged,
}: {
  funnel: Funnel;
  canTurnOn: boolean;
  canTurnOff: boolean;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const on = funnel.enabled;
  const allowed = on ? canTurnOff : canTurnOn;
  const expected = on ? TURN_OFF_PHRASE : funnel.enablePhrase;
  const failing = funnel.readiness.filter((r) => !r.ok);
  const canConfirm = typed === expected && (on || funnel.ready) && !busy;

  const close = () => {
    setOpen(false);
    setTyped('');
    setError(null);
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await api('/connectiq/deploy-funnel', {
        method: 'POST',
        body: JSON.stringify({ enabled: !on, confirm: on ? undefined : typed }),
      });
      close();
      onChanged();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ciq-funnel">
      <div className="ciq-funnel-head">
        <div>
          <div className="ciq-eyebrow">SSH funnel</div>
          <div className={on ? 'ciq-funnel-state on' : 'ciq-funnel-state'}>{on ? 'On' : 'Off'}</div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label="SSH funnel"
          className={on ? 'ciq-switch on' : 'ciq-switch'}
          disabled={!allowed}
          onClick={() => setOpen(true)}
          title={allowed ? undefined : on ? 'ADMIN or SUPERADMIN can turn this off' : 'Only SUPERADMIN can turn this on'}
        >
          <span className="ciq-switch-knob" aria-hidden="true" />
        </button>
      </div>
      <p className="ciq-muted">
        {on
          ? 'Direct production actions from PlatformIQ are refused. They must go through jump01, which is not wired yet.'
          : 'Off. Deploys go direct, as they do today. Turning this on needs SUPERADMIN, every readiness check, and a typed confirmation.'}
      </p>

      <Modal open={open} onClose={close} title={on ? 'Turn the SSH funnel off?' : 'Turn the SSH funnel on?'}>
        {on ? (
          <p>Direct production actions will be allowed again. Type <strong>{TURN_OFF_PHRASE}</strong> to confirm.</p>
        ) : (
          <>
            <p>This refuses direct production actions and routes them through jump01. Nothing changes until every check passes and you type the phrase.</p>
            <ul className="ciq-checks">
              {funnel.readiness.map((r) => (
                <li key={r.key} className={r.ok ? 'ok' : 'fail'}>
                  <span aria-hidden="true">{r.ok ? '✓' : '✗'}</span>
                  <span>
                    {r.label}
                    <span className="ciq-muted small"> — {r.detail}</span>
                  </span>
                </li>
              ))}
            </ul>
            {failing.length > 0 && (
              <p className="ciq-warn">
                {failing.length} check{failing.length === 1 ? '' : 's'} failing. The funnel cannot be turned on until all pass.
              </p>
            )}
          </>
        )}
        <label className="ciq-field">
          <span>Type {expected} to confirm</span>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} />
        </label>
        <div className="ciq-actions">
          <button type="button" className="ghost" onClick={close}>
            Cancel
          </button>
          <button type="button" className={on ? 'ciq-danger' : undefined} onClick={confirm} disabled={!canConfirm}>
            {busy ? 'Working…' : on ? 'Turn funnel off' : 'Turn funnel on'}
          </button>
        </div>
        {error && <p className="form-error" role="alert">{error}</p>}
      </Modal>
    </div>
  );
}
